import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Search,
  Refresh,
  History,
  BugReport,
  DeleteSweep,
  Visibility,
  ContentCopy,
  InfoOutlined,
  CheckCircle,
  WarningAmber,
  ErrorOutline
} from '@mui/icons-material';
import {
  getLogStats,
  getActivityLogs,
  getErrorLogs,
  purgeOldLogs
} from '../services/logs';
import { useThemeMode } from '../context/ThemeContext';

export default function Logs() {
  const { isDarkMode } = useThemeMode();
  const [tabValue, setTabValue] = useState(0); // 0: Activity, 1: Errors
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Activity logs state
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityCount, setActivityCount] = useState(0);
  const [activityPage, setActivityPage] = useState(0);
  const [activityRowsPerPage, setActivityRowsPerPage] = useState(25);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityUserType, setActivityUserType] = useState('all');

  // Error logs state
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorCount, setErrorCount] = useState(0);
  const [errorPage, setErrorPage] = useState(0);
  const [errorRowsPerPage, setErrorRowsPerPage] = useState(25);
  const [errorSearch, setErrorSearch] = useState('');
  const [errorStatus, setErrorStatus] = useState('all');

  // Dialog state
  const [selectedLog, setSelectedLog] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [purging, setPurging] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Load stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await getLogStats();
      if (res && res.data) {
        setStats(res.data);
        if (res.data.totalErrors !== undefined) {
          setErrorCount((prev) => (errorSearch || errorStatus !== 'all' ? prev : res.data.totalErrors));
        }
        if (res.data.totalActivities !== undefined) {
          setActivityCount((prev) => (activitySearch || activityUserType !== 'all' ? prev : res.data.totalActivities));
        }
      }
    } catch (err) {
      console.error('Failed to fetch log stats:', err);
    }
  }, [activitySearch, activityUserType, errorSearch, errorStatus]);

  // Load Activity Logs
  const fetchActivity = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getActivityLogs({
        page: activityPage + 1,
        limit: activityRowsPerPage,
        userType: activityUserType,
        search: activitySearch
      });
      if (res && res.data) {
        setActivityLogs(res.data);
        setActivityCount(res.count || 0);
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load activity logs', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [activityPage, activityRowsPerPage, activityUserType, activitySearch]);

  // Load Error Logs
  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getErrorLogs({
        page: errorPage + 1,
        limit: errorRowsPerPage,
        statusCode: errorStatus,
        search: errorSearch
      });
      if (res && res.data) {
        setErrorLogs(res.data);
        setErrorCount(res.count || 0);
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load error logs', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [errorPage, errorRowsPerPage, errorStatus, errorSearch]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchActivity();
    } else {
      fetchErrors();
    }
  }, [tabValue, fetchActivity, fetchErrors]);

  const handleRefresh = () => {
    fetchStats();
    if (tabValue === 0) {
      fetchActivity();
    } else {
      fetchErrors();
    }
    setSnackbar({ open: true, message: 'Logs refreshed', severity: 'success' });
  };

  const handleOpenDetail = (log) => {
    setSelectedLog(log);
    setDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setSelectedLog(null);
    setDialogOpen(false);
  };

  const handleConfirmPurge = async () => {
    setPurging(true);
    try {
      await purgeOldLogs();
      setSnackbar({
        open: true,
        message: `Logs older than ${stats?.retentionDays || 20} days purged successfully`,
        severity: 'success'
      });
      setPurgeDialogOpen(false);
      handleRefresh();
    } catch (err) {
      setSnackbar({ open: true, message: 'Purge failed: ' + (err.message || 'Unknown error'), severity: 'error' });
    } finally {
      setPurging(false);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setSnackbar({ open: true, message: 'Copied to clipboard', severity: 'info' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getUserTypeColor = (type) => {
    switch (type) {
      case 'admin': return { bg: '#ede9fe', color: '#6d28d9' };
      case 'merchant': return { bg: '#e0f2fe', color: '#0369a1' };
      default: return { bg: '#ecfdf5', color: '#047857' };
    }
  };

  const getStatusColor = (code) => {
    if (code >= 500) return { bg: '#fee2e2', color: '#b91c1c' };
    if (code >= 400) return { bg: '#fef3c7', color: '#b45309' };
    if (code >= 300) return { bg: '#e0e7ff', color: '#4338ca' };
    return { bg: '#dcfce7', color: '#15803d' };
  };

  const displayActivityCount = (activitySearch || activityUserType !== 'all')
    ? activityCount
    : (stats?.totalActivities ?? activityCount);

  const displayErrorCount = (errorSearch || errorStatus !== 'all')
    ? errorCount
    : (stats?.totalErrors ?? errorCount);

  return (
    <Box sx={{ p: 1 }}>
      {/* Overview Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{
            borderRadius: 3,
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0,0,0,0.08)',
            background: isDarkMode 
              ? 'linear-gradient(135deg, #1e293b 0%, #172033 100%)' 
              : 'linear-gradient(135deg, #fafcfb 0%, #f1f5f3 100%)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                  TOTAL ACTIVITY LOGS
                </Typography>
                <History sx={{ color: '#52ad83' }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: isDarkMode ? '#e2e8f0' : '#1e293b', mt: 1 }}>
                {stats?.totalActivities ?? '-'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#059669', display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <CheckCircle sx={{ fontSize: 14, mr: 0.5 }} />
                +{stats?.activities24h ?? 0} in last 24h
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{
            borderRadius: 3,
            border: isDarkMode ? '1px solid rgba(239, 68, 68, 0.25)' : '1px solid rgba(239, 68, 68, 0.15)',
            background: isDarkMode 
              ? 'linear-gradient(135deg, #241d24 0%, #1e1820 100%)' 
              : 'linear-gradient(135deg, #fdf8f8 0%, #fbf1f1 100%)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#f87171' : '#b91c1c', fontWeight: 600 }}>
                  SYSTEM ERROR LOGS
                </Typography>
                <BugReport sx={{ color: '#ef4444' }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: isDarkMode ? '#f87171' : '#b91c1c', mt: 1 }}>
                {stats?.totalErrors ?? '-'}
              </Typography>
              <Typography variant="caption" sx={{ color: isDarkMode ? '#fca5a5' : '#dc2626', display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <ErrorOutline sx={{ fontSize: 14, mr: 0.5 }} />
                {stats?.errors500 ?? 0} Critical (500s) | {stats?.errors400 ?? 0} Client (4xx)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{
            borderRadius: 3,
            border: isDarkMode ? '1px solid rgba(82, 173, 131, 0.25)' : '1px solid rgba(82, 173, 131, 0.15)',
            background: isDarkMode 
              ? 'linear-gradient(135deg, #182620 0%, #13201a 100%)' 
              : 'linear-gradient(135deg, #f7fbf9 0%, #eff7f2 100%)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#4ade80' : '#15803d', fontWeight: 600 }}>
                  AUTO-RETENTION POLICY
                </Typography>
                <DeleteSweep sx={{ color: '#16a34a' }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: isDarkMode ? '#4ade80' : '#15803d', mt: 1 }}>
                {stats?.retentionDays ?? 20} Days
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: isDarkMode ? '#86efac' : '#166534' }}>
                  Auto-purges older records daily
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => setPurgeDialogOpen(true)}
                  sx={{ textTransform: 'none', py: 0, px: 1, fontSize: '0.75rem' }}
                >
                  Purge Now
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table Card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0,0,0,0.08)',
          boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.25)' : '0 4px 20px rgba(0,0,0,0.04)',
          overflow: 'hidden'
        }}
      >
        {/* Header toolbar */}
        <Box sx={{ p: 2.5, borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Tabs
            value={tabValue}
            onChange={(e, val) => setTabValue(val)}
            sx={{
              '& .MuiTabs-indicator': { backgroundColor: '#52ad83', height: 3 },
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem' },
              '& .Mui-selected': { color: '#52ad83 !important' }
            }}
          >
            <Tab icon={<History sx={{ fontSize: 18 }} />} iconPosition="start" label={`Activity Logs (${displayActivityCount})`} />
            <Tab icon={<BugReport sx={{ fontSize: 18 }} />} iconPosition="start" label={`Error Logs (${displayErrorCount})`} />
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {/* Search */}
            <TextField
              size="small"
              placeholder={tabValue === 0 ? "Search action, entity, user..." : "Search message, endpoint..."}
              value={tabValue === 0 ? activitySearch : errorSearch}
              onChange={(e) => {
                if (tabValue === 0) {
                  setActivitySearch(e.target.value);
                  setActivityPage(0);
                } else {
                  setErrorSearch(e.target.value);
                  setErrorPage(0);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#9ca3af', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 260 }}
            />

            {/* Filter Dropdown */}
            {tabValue === 0 ? (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Role Filter</InputLabel>
                <Select
                  value={activityUserType}
                  label="Role Filter"
                  onChange={(e) => {
                    setActivityUserType(e.target.value);
                    setActivityPage(0);
                  }}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="user">User (Customer)</MenuItem>
                  <MenuItem value="merchant">Merchant</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status Code</InputLabel>
                <Select
                  value={errorStatus}
                  label="Status Code"
                  onChange={(e) => {
                    setErrorStatus(e.target.value);
                    setErrorPage(0);
                  }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="500">500 Server Errors</MenuItem>
                  <MenuItem value="400">400 Client Errors</MenuItem>
                  <MenuItem value="401">401 Unauthorized</MenuItem>
                  <MenuItem value="403">403 Forbidden</MenuItem>
                  <MenuItem value="404">404 Not Found</MenuItem>
                </Select>
              </FormControl>
            )}

            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} sx={{ color: '#52ad83' }}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
            <CircularProgress sx={{ color: '#52ad83' }} />
          </Box>
        ) : tabValue === 0 ? (
          /* ================= ACTIVITY LOGS TABLE ================= */
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f9fafb' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#4b5563' }}>TIMESTAMP</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b5563' }}>ACTOR</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b5563' }}>ACTION</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b5563' }}>TARGET ENTITY</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b5563' }}>IP ADDRESS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#4b5563' }}>DETAILS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activityLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#6b7280' }}>
                      <InfoOutlined sx={{ fontSize: 40, color: '#9ca3af', mb: 1, display: 'block', mx: 'auto' }} />
                      No activity logs found.
                    </TableCell>
                  </TableRow>
                ) : (
                  activityLogs.map((log) => {
                    const typeStyle = getUserTypeColor(log.userType);
                    return (
                      <TableRow key={log.id} hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#4b5563', whiteSpace: 'nowrap' }}>
                          {formatDate(log.createdAt)}
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={`${(log.userType || 'user').toUpperCase()} ${log.userId ? `#${log.userId}` : ''}`}
                            sx={{
                              backgroundColor: typeStyle.bg,
                              color: typeStyle.color,
                              fontWeight: 700,
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                            {log.action}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {log.entityType ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${log.entityType} ${log.entityId ? `#${log.entityId}` : ''}`}
                              sx={{ fontSize: '0.75rem', borderColor: '#d1d5db' }}
                            />
                          ) : (
                            <Typography variant="caption" sx={{ color: '#9ca3af' }}>-</Typography>
                          )}
                        </TableCell>

                        <TableCell sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          {log.ipAddress || '-'}
                        </TableCell>

                        <TableCell align="right">
                          <Tooltip title="View JSON metadata">
                            <IconButton size="small" onClick={() => handleOpenDetail(log)}>
                              <Visibility sx={{ fontSize: 18, color: '#6b7280' }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={activityCount}
              rowsPerPage={activityRowsPerPage}
              page={activityPage}
              onPageChange={(e, newPage) => setActivityPage(newPage)}
              onRowsPerPageChange={(e) => {
                setActivityRowsPerPage(parseInt(e.target.value, 10));
                setActivityPage(0);
              }}
            />
          </TableContainer>
        ) : (
          /* ================= ERROR LOGS TABLE ================= */
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f9fafb' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#4b5563' }}>TIMESTAMP</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b5563' }}>STATUS</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b5563' }}>ENDPOINT</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b5563' }}>MESSAGE</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4b5563' }}>ACTOR</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#4b5563' }}>INSPECT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#6b7280' }}>
                      <CheckCircle sx={{ fontSize: 40, color: '#10b981', mb: 1, display: 'block', mx: 'auto' }} />
                      No system errors logged. The server is healthy!
                    </TableCell>
                  </TableRow>
                ) : (
                  errorLogs.map((log) => {
                    const statusStyle = getStatusColor(log.statusCode);
                    return (
                      <TableRow key={log.id} hover sx={{ '&:hover': { backgroundColor: '#fef2f2' } }}>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#4b5563', whiteSpace: 'nowrap' }}>
                          {formatDate(log.createdAt)}
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={log.statusCode}
                            sx={{
                              backgroundColor: statusStyle.bg,
                              color: statusStyle.color,
                              fontWeight: 800,
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {log.method && (
                              <Chip
                                size="small"
                                label={log.method}
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#f3f4f6' }}
                              />
                            )}
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937' }}>
                              {log.endpoint || '-'}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ maxWidth: 350 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ color: '#b91c1c', fontWeight: 500 }}
                            title={log.message}
                          >
                            {log.message}
                          </Typography>
                          {log.errorCode && (
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              Code: {log.errorCode}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          {log.userId ? `${log.userType || 'user'} #${log.userId}` : 'Guest'}
                        </TableCell>

                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Visibility sx={{ fontSize: 14 }} />}
                            onClick={() => handleOpenDetail(log)}
                            sx={{ textTransform: 'none', py: 0.2, px: 1, fontSize: '0.75rem' }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={errorCount}
              rowsPerPage={errorRowsPerPage}
              page={errorPage}
              onPageChange={(e, newPage) => setErrorPage(newPage)}
              onRowsPerPageChange={(e) => {
                setErrorRowsPerPage(parseInt(e.target.value, 10));
                setErrorPage(0);
              }}
            />
          </TableContainer>
        )}
      </Paper>

      {/* ================= DETAIL MODAL DIALOG ================= */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedLog && 'statusCode' in selectedLog ? (
              <>
                <BugReport sx={{ color: '#ef4444' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Error Details (HTTP {selectedLog.statusCode})
                </Typography>
              </>
            ) : (
              <>
                <History sx={{ color: '#52ad83' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Activity Event Details
                </Typography>
              </>
            )}
          </Box>
          <IconButton size="small" onClick={handleCloseDetail}>
            &times;
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          {selectedLog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Common Info */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Timestamp</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(selectedLog.createdAt)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>IP Address</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedLog.ipAddress || 'Not recorded'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>User / Actor</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedLog.userId ? `${(selectedLog.userType || 'user').toUpperCase()} #${selectedLog.userId}` : 'Unauthenticated'}
                  </Typography>
                </Grid>
                {'action' in selectedLog && (
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>Action</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>{selectedLog.action}</Typography>
                  </Grid>
                )}
                {'endpoint' in selectedLog && (
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>Endpoint</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                      {selectedLog.method} {selectedLog.endpoint}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* Error Message */}
              {'message' in selectedLog && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>Error Message</Typography>
                  <Paper sx={{ p: 1.5, backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 600 }}>
                      {selectedLog.message}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Stack Trace */}
              {selectedLog.stackTrace && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>Stack Trace</Typography>
                    <Button
                      size="small"
                      startIcon={<ContentCopy sx={{ fontSize: 14 }} />}
                      onClick={() => copyToClipboard(selectedLog.stackTrace)}
                      sx={{ textTransform: 'none', py: 0, fontSize: '0.7rem' }}
                    >
                      Copy Trace
                    </Button>
                  </Box>
                  <Paper sx={{
                    p: 1.5,
                    backgroundColor: '#1f2937',
                    borderRadius: 2,
                    maxHeight: 220,
                    overflowY: 'auto'
                  }}>
                    <Typography component="pre" sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      color: '#f87171',
                      m: 0,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {selectedLog.stackTrace}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Request Payload */}
              {selectedLog.reqBody && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>Sanitized Request Body</Typography>
                  <Paper sx={{
                    p: 1.5,
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    maxHeight: 180,
                    overflowY: 'auto'
                  }}>
                    <Typography component="pre" sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      color: '#334155',
                      m: 0
                    }}>
                      {JSON.stringify(selectedLog.reqBody, null, 2)}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Activity Metadata */}
              {selectedLog.metadata && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>Activity Metadata</Typography>
                  <Paper sx={{
                    p: 1.5,
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    maxHeight: 180,
                    overflowY: 'auto'
                  }}>
                    <Typography component="pre" sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      color: '#334155',
                      m: 0
                    }}>
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #f3f4f6' }}>
          <Button onClick={handleCloseDetail} sx={{ textTransform: 'none', color: '#6b7280' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= PURGE CONFIRMATION DIALOG ================= */}
      <Dialog
        open={purgeDialogOpen}
        onClose={() => setPurgeDialogOpen(false)}
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber sx={{ color: '#f59e0b' }} />
          Confirm Log Purge
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#4b5563' }}>
            This will permanently delete all activity and error logs older than <strong>{stats?.retentionDays || 20} days</strong> from your MySQL database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPurgeDialogOpen(false)} sx={{ textTransform: 'none', color: '#6b7280' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmPurge}
            disabled={purging}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {purging ? <CircularProgress size={20} color="inherit" /> : 'Purge Logs Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
